<script setup>
import { ref, computed, onMounted } from 'vue';
import { useUserStore } from '@js/vue/domain/user/stores/useUserStore';
import { getBrandBalance } from "@js/api/domain/brand";
import {formatCurrency} from "@js/utilities/formatters";

const props = defineProps({
    isCard: Boolean
})

const userStore = useUserStore();

const brandBalanceInfo = ref(null);
const isLoading = ref(true);
const hasError = ref(false);

const hasCredit = computed(() => {
    if (!brandBalanceInfo.value) return false;

    const rawBalance = Number(brandBalanceInfo.value.balance);
    return rawBalance < 0;
});

const managerFirstName = computed(() => {
    const managerName = brandBalanceInfo.value?.clientManager?.name;
    if (!managerName) return 'Manager';

    return managerName.split(' ')[0];
});

const formattedBalance = computed(() => {
    if (!brandBalanceInfo.value) return '$0.00';

    const rawBalance = Number(brandBalanceInfo.value.balance);
    const displayAmount = Math.abs(rawBalance);

    return formatCurrency(displayAmount);
});

const mailtoLink = computed(() => {
    const manager = brandBalanceInfo.value?.clientManager;
    if (!manager?.email) return '#';

    const brandName = brandBalanceInfo.value?.name || 'Brand';
    const subject = encodeURIComponent(`Question about applying Brand Credit (${brandName})`);

    return `mailto:${manager.email}?subject=${subject}`;
});

const fetchBrandData = async () => {
    const brandId = userStore.brandID;

    if (!brandId) {
        hasError.value = true;
        isLoading.value = false;
        return;
    }

    isLoading.value = true;
    hasError.value = false;

    try {
        const { data } = await getBrandBalance(brandId);
        brandBalanceInfo.value = data;
    } catch (err) {
        hasError.value = true;
        brandBalanceInfo.value = null;
        console.error(`Failed to fetch balance for brand ${brandId}:`, err?.response?.status || err.message);
    } finally {
        isLoading.value = false;
    }
};

onMounted(() => {
    fetchBrandData();
});

</script>

<template>
    <!-- Render outer wrapper only during loading OR when brand data exists with no error -->
    <div v-if="isLoading || (brandBalanceInfo && !hasError) && !brandBalanceInfo.isUserRestricted" class="brand-balance-wrapper" :class="{ 'w-100': hasCredit || isCard }">

        <!-- Loading State -->
        <div v-if="isLoading" class="card card-body text-center text-muted border-0 bg-light">
            <div class="d-flex align-items-center justify-content-center gap-2">
                <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
                <span class="small">Loading balance details...</span>
            </div>
        </div>

        <!-- Data State -->
        <div v-else-if="brandBalanceInfo">

            <!-- CREDIT / NEGATIVE BALANCE -->
            <div v-if="hasCredit" class="card credit-card border-success shadow-sm w-100">
                <div class="card-body p-2 p-md-3">

                    <div class="d-flex flex-column justify-content-between gap-2 gap-md-4" :class="{'flex-md-row align-items-md-center': !isCard}">

                        <div class="d-flex flex-column flex-lg-row align-items-lg-center gap-2 flex-grow-1">

                            <div class="d-flex align-items-center gap-3 shrink-0">

                                <div
                                    class="d-flex gap-2"
                                    :class="isCard ? 'flex-column align-items-start' : 'align-items-center'"
                                >
                                    <span class="fw-bold text-success text-uppercase tracking-wide small">
                                        {{ brandBalanceInfo.name }}
                                    </span>

                                    <div class="d-flex flex-column gap-2">
                                        <span class="badge bg-success-subtle text-success border border-success-subtle px-2 py-0-5 rounded-pill small">
                                            Credit Available
                                        </span>

                                        <h3 class="fs-2 fw-bold text-success mb-0 lh-1 mt-1">
                                            {{ formattedBalance }}
                                        </h3>
                                    </div>
                                </div>

                            </div>

                            <!-- Vertical Divider (Hidden on small screens) -->
                            <div class="vr d-none d-lg-block mx-2 text-success opacity-25" style="min-height: 2.5rem;"></div>

                            <div>
                                <p class="card-text text-success-emphasis small mb-0 max-w-prose">
                                    You currently have an available credit! Reach out to your client manager to discuss applying it toward your next invoice.
                                </p>
                            </div>

                        </div>

                        <!-- Right Column: Client Manager Box -->
                        <div
                            v-if="brandBalanceInfo.graphicArtist"
                            class="shrink-0 ms-md-auto"
                            :class="{ 'w-100': isCard }"
                        >
                            <div class="card border-success-subtle bg-white shadow-2xs">
                                <div class="card-body py-2 px-3 d-flex align-items-center gap-3" :class="{'justify-content-between' : isCard}">
                                    <div class="d-flex flex-column">
                                        <span class="text-uppercase text-muted fw-semibold small line-height-1 text-nowrap">
                                            Client Manager
                                        </span>
                                        <span class="fw-medium text-dark small text-nowrap">
                                            {{ brandBalanceInfo.graphicArtist.name }}
                                        </span>
                                    </div>

                                    <a :href="mailtoLink" class="btn btn-sm btn-success fw-semibold text-nowrap py-1 px-2.5"
                                    >Contact {{ managerFirstName }}</a>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- REGULAR BALANCE -->
            <div v-else class="card bg-light border-light-subtle">
                <div class="card-body py-2 px-3 d-flex align-items-center justify-content-between gap-4">
                    <div class="d-flex flex-column">
                        <span class="fw-bold text-dark small">{{ brandBalanceInfo.name }}</span>
                        <span class="text-muted small uppercase">Current Total Balance</span>
                    </div>

                    <span class="fs-5 fw-bold text-dark">{{ formattedBalance }}</span>
                </div>
            </div>

        </div>
    </div>
</template>

<style scoped>

/* Custom credit background tinting */
.credit-card {
    background-color: #f0fdf4;
    border-width: 2px !important;
}

.tracking-wide {
    letter-spacing: 0.05em;
}

.shadow-2xs {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
</style>

